/**
 * Produits les plus vendus, avec leur évolution.
 *
 * Un tableau et non un graphique à barres : chaque ligne porte cinq chiffres
 * (unités, CA, deux écarts) et un nom de produit parfois long. Des barres n'en
 * montreraient qu'un.
 *
 * Écarts calculés sur le CA du MÊME produit dans la fenêtre de référence. Un
 * produit absent de la référence est « nouveau », pas « +∞ % ».
 */

import { formatInteger, formatMoney } from '../lib/format';
import { relativeChange } from '../lib/sales';
import { IMPACT_TONE_COLORS } from '../lib/theme';
import { impactTone, relativeVerdict, verdictGlyph, verdictLabel } from '../lib/verdict';
import type { ProductSalesFigures, TopProductRow } from '../types';
import { Card } from './Card';

interface TopProductsTableProps {
  rows: readonly TopProductRow[];
  currency: string;
  /** Période et an dernier confondus (période ≥ 1 an) : une seule colonne d'écart. */
  comparisonsOverlap: boolean;
}

function DeltaCell({ current, reference }: { current: ProductSalesFigures; reference: ProductSalesFigures }) {
  if (reference.units === 0 && reference.revenue === 0) {
    return <td className="num" style={{ color: IMPACT_TONE_COLORS.neutral }}>nouveau</td>;
  }

  const change = relativeChange(current.revenue, reference.revenue);
  const verdict = relativeVerdict(change, 5);
  const text = change === null ? '—' : `${change >= 0 ? '+' : '−'}${Math.abs(change).toLocaleString('fr-FR')} %`;

  return (
    <td className="num" style={{ color: IMPACT_TONE_COLORS[impactTone(verdict, true)] }}>
      <span aria-hidden="true">{verdictGlyph(verdict)}</span> {text}
      <span className="sr-only"> — {verdictLabel(verdict)}</span>
    </td>
  );
}

export function TopProductsTable({ rows, currency, comparisonsOverlap }: TopProductsTableProps) {
  return (
    <Card
      title="Produits les plus vendus"
      subtitle="Classés par chiffre d'affaires de la période, remises déduites, TTC"
      className="chart-grid__wide"
    >
      {rows.length === 0 ? (
        <p className="empty">Aucune vente sur cette période.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Produit</th>
                <th className="num">Unités</th>
                <th className="num">CA TTC</th>
                {!comparisonsOverlap && <th className="num">vs période préc.</th>}
                <th className="num">vs an dernier</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.productKey}>
                  <td className="wrap">{row.title}</td>
                  <td className="num">{formatInteger(row.current.units)}</td>
                  <td className="num">{formatMoney(row.current.revenue, currency)}</td>
                  {!comparisonsOverlap && <DeltaCell current={row.current} reference={row.previous} />}
                  <DeltaCell current={row.current} reference={row.yearAgo} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
